<template>
    <div class="input-item">
        <label v-if="label" class="input-item__label">{{ label }}</label>

        <div v-if="RulePassword" class="input-item__password-reqs">
            <p class="input-item__password-reqs__item" :class="{ 'input-item__password-reqs__item--valid': contains_chars }">{{ $t('inc_chars') }}</p>
            <p class="input-item__password-reqs__item" :class="{ 'input-item__password-reqs__item--valid': contains_number }">{{ $t('inc_number') }}</p>
            <p class="input-item__password-reqs__item" :class="{ 'input-item__password-reqs__item--valid': valid_password_length }">{{ $t('inc_password_length') }}</p>
        </div>

        <input
            v-model="inputValue"
            type="password"
            class="input-item__input input"
            :class="{
                'input-item--error': v$ && v$.$error
            }"
            @update:modelValue="change"
        />

        <p v-if="RulePassword" style="font-size: 0.75em;">{{ $t('password_helper_text') }}</p>

        <div class="input-item__error" v-if="v$ && v$.$error">
            <span v-if="v$.inputValue.required" class="input-item__error__msg">{{ locale === 'en' ? 'This field is mandatory' : 'Fältet är obligatoriskt'}}</span>
            <span v-if="v$.inputValue.validationPassword" class="input-item__error__msg">{{ locale === 'en' ? 'Invalid password' : 'Ogiltigt lösenord'}}</span>
        </div>

        <span
            v-if="RulePassword"
            class="input-item__password-length"
            :class="{
                'input-item__password-length--valid': valid_password_length,
                'input-item__password-length--show': typed
            }"
        >
            {{ password_length }}
        </span>

        <div
            class="input-item__password-valid"
            :class="{ 'input-item__password-valid--show' : valid_password }"
        >
            <svg width="100%" height="100%" viewBox="0 0 140 100">
                <path
                    d="M10,50 l25,40 l95,-70"
                    class="tick"
                    :class="{ checked: valid_password }"
                />
            </svg>
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, reactive, toRefs, computed, watchEffect } from 'vue'
    import { required } from '@vuelidate/validators'
    import useVuelidate from '@vuelidate/core'
    import ValidationPassword from '@/validators/validation-password'
    import { useI18n } from 'vue-i18n'

    export default defineComponent({
        props: {
            modelValue: {
                type: String,
                required: false
            },
            validators: {
                type: String,
                required: false
            },
            label: {
                type: String,
                required: false,
            }
        },
        emits: ['update:modelValue'],
        setup(props, { emit }) {
            const { locale } = useI18n()
            let v$ = useVuelidate()

            const hasValidation      = props.validators !== undefined
            const RuleRequired       = props.validators?.includes('required')
            const RulePassword       = props.validators?.includes('password')
            const validationPassword = (value: string) => ValidationPassword.validate(value).password_valid

            const data = reactive({
                password: null as null | string,
                password_length: 0,
                typed: false,
                contains_chars: false,
                contains_number: false,
                valid_password_length: false,
                valid_password: false
            })

            const state = reactive({ inputValue: '' })

            const change = (value: string) => {
                if (hasValidation) {
                    const rules = ValidationPassword.validate(value)

                    data.password_length       = value.length
                    data.valid_password_length = data.password_length > 5
                    data.typed                 = data.password_length > 0

                    data.contains_chars  = rules.contains_chars
                    data.contains_number = rules.contains_number

                    // Check if the password is valid
                    if (rules.contains_chars && rules.contains_number && rules.validLength) {
                        data.valid_password = true
                    }

                    touch()
                }

                emit('update:modelValue', state.inputValue)
            }

            const reset = () => {
                if (hasValidation) {
                    state.inputValue = ''

                    v$.value.inputValue.$reset()
                }
            }

            const touch = () => {
                v$.value.inputValue.$touch()
            }

            if (hasValidation) {
                let activeRoles: any = {}

                if (RuleRequired) {
                    activeRoles.inputValue = { required }
                }

                if (RulePassword) {
                    activeRoles.inputValue = { validationPassword }
                }

                const rules = computed(() => activeRoles)

                v$ = useVuelidate(rules, state)
            }

            watchEffect(() => {
                if (props.modelValue) {
                    state.inputValue = props.modelValue
                }
            })

            return {
                v$,
                ...toRefs(data),
                ...toRefs(state),
                RulePassword,
                change,
                reset,
                touch,
                locale
            }
        }
    })
</script>
